/**
 * @file
 * Global config
 */

// Types
import { AnglerId, RefreshRate } from './types';

/**
 * @Bass-Blocks-Integration
 * Hard coding env variables to 'local' for now.
 * Checking for `process.env` breaks browser js. The global process object only exists in server side Node environments.
 */
// const { NODE_ENV, REACT_APP_API_MOCK } = process.env;
const NODE_ENV = 'local';
const REACT_APP_API_MOCK = 'local';
const isDev = NODE_ENV === 'development';

export let API_BASE = '//bass-node-app.docksal:3001/v1';

if (REACT_APP_API_MOCK === 'stage') {
  API_BASE = '//api.stage.bassmasterdata.com/v1';
}

// Allow override of API_BASE from within consuming application, like Drupal
export function setApiBase(this: any, newUri: string) {
  API_BASE = newUri;
  return this;
}

export const CMS_URL = 'https://www.bassmaster.com';
export const DEFAULT_STRING_NO_TABLE_RESULTS = 'No results at this time.';

// Used for table columns
export const BIG_BASS_LEADER_DESCRIPTION = 'Phoenix Boats Big Bass Leader';
export const BERKLEY_BIG_BASS_LEADER_DESCRIPTION = 'Berkley Big Bass Leader';
export const CLASS_PREFIX = 'bass-table__';
export const NO_CATCHES_TEXT = 'No Activity';

/**
 * Returns a photo url for the angler based on id / tms value.
 */
export const getPhotoById = (anglerId: AnglerId) =>
  `${isDev ? CMS_URL : ''}/api/v1/persons/action/${anglerId}`;

/**
 * Returns a profile url based on id / tms value.
 */
export const getProfileUrlById = (anglerId: AnglerId) =>
  `${isDev ? CMS_URL : ''}/angler/profile/${anglerId}`;

/**
 * Get the full size portrait
 */
export const getPortraitById = (anglerId: AnglerId) =>
  `${isDev ? CMS_URL : ''}/api/v1/persons/portrait/${anglerId}`;

/**
 * CMS realtime leaderboard portrait
 * Smaller version of the full sized portrait
 */
export const getRTLPortraitById = (anglerId: AnglerId) =>
  `${getPortraitById(anglerId)}/rtl`;

// CMS routes used in various components.
export const CMS_ANGLERS_ROUTE = `/anglers`;
export const CMS_AOY_STANDINGS_ROUTE = `/aoy/standings`;

// Tournament constants
export const KEEPERS_MAX_COUNT = 5;

// Global polling rate used for data refresh checks
export const GLOBAL_POLLING_RATE = 5000;

// AoY
export const AOY_START_YEAR = 2006;

// Shared refresh rates for Leaderboard, WeightLeader, AtTheTanks, OnStage
export const SHARED_REFRESH_RATE: RefreshRate = [15000, 15000];

// Time span to show highlighting for catches
export const CATCH_TIMESPAN = 240000;

// Default time zone of all catch data
export const CATCH_TIMEZONE = 'America/New_York';

// Standard format for time
export const TIME_FORMAT = 'h:mm aa';

// Default cutline message.
export const DEFAULT_CUTLINE_MESSAGE = 'Cutline';

// Aoy section definitions.
export const AoySections = {
  91: {"name": "Elite", "class": "aoy" },
  90: {"name": "ROY", "class": "roy" },
  99: {"name": "EQs", "class": "eqs" },
  501: {"name": "EQs", "class": "opens" },
  511: {"name": "NORTHERN OPENS", "class": "northern" },
  521: {"name": "SOUTHERN OPENS", "class": "southern" },
  531: {"name": "EASTERN OPENS", "class": "eastern" },
  541: {"name": "WESTERN OPENS", "class": "western" },
  551: {"name": "CENTRAL OPENS", "class": "central" },
  1191: {"name": "1 Opens", "class": "d1" },
  1201: {"name": "2 Opens", "class": "d2" },
  1211: {"name": "3 Opens", "class": "d3" },
  1: {"name": "KAYAK", "class": "kayak" },
}

// Aoy select options.
export const AoyTournamentSelect = {
  // If array length is greater than 1, values are added as select options to `.aoy-type-selector` dropdown.
  // Logic and templating happen in `AoyFull.tsx`, look for AoySelectOptions.
  1191: [[1191, 'OPENS DIV. 1 PRO'], [1190, 'OPENS DIV. 1 CO']],
  1190: [[1191, 'OPENS DIV. 1 PRO'], [1190, 'OPENS DIV. 1 CO']],
  1201: [[1201, 'OPENS DIV. 2 PRO'], [1200, 'OPENS DIV. 2 CO']],
  1200: [[1201, 'OPENS DIV. 2 PRO'], [1200, 'OPENS DIV. 2 CO']],
  1211: [[1211, 'OPENS DIV. 3 PRO'], [1210, 'OPENS DIV. 3 CO']],
  1210: [[1211, 'OPENS DIV. 3 PRO'], [1210, 'OPENS DIV. 3 CO']],
  91: [[91, 'AOY']],
  90: [[90, 'ROY']],
  99: [[99, 'EQS']],
  501: [[501, 'OPENS PRO'], [500, 'OPENS CO']],
  500: [[501, 'OPENS PRO'], [500, 'OPENS CO']],
  531: [[531, 'EASTERN OPENS PRO'], [530, 'EASTERN OPENS CO']],
  530: [[531, 'EASTERN OPENS PRO'], [530, 'EASTERN OPENS CO']],
  511: [[511, 'NORTHERN OPENS PRO'], [510, 'NORTHERN OPENS CO']],
  510: [[511, 'NORTHERN OPENS PRO'], [510, 'NORTHERN OPENS CO']],
  551: [[551, 'CENTRAL OPENS PRO'], [550, 'CENTRAL OPENS CO']],
  550: [[551, 'CENTRAL OPENS PRO'], [550, 'CENTRAL OPENS CO']],
  521: [[521, 'SOUTHERN OPENS PRO'], [520, 'SOUTHERN OPENS CO']],
  520: [[521, 'SOUTHERN OPENS PRO'], [520, 'SOUTHERN OPENS CO']],
  541: [[541, 'WESTERN OPENS PRO'], [540, 'WESTERN OPENS CO']],
  540: [[541, 'WESTERN OPENS PRO'], [540, 'WESTERN OPENS CO']]
}

// Aoy series per year.
export const AoySeriesYear = {
  //2005: [91, 90, 511, 521, 541, 551],
  2006: [91, 90, 511, 521],
  2007: [91, 90, 521, 551],
  2008: [91, 90, 521, 551],
  2009: [91, 90, 511, 521, 551],
  2010: [91, 90, 511, 521, 551],
  2011: [91, 90, 511, 521, 551],
  2012: [91, 90, 511, 521, 551],
  2013: [91, 90, 511, 521, 551],
  2014: [91, 90, 511, 521, 551],
  2015: [91, 90, 511, 521, 551],
  2016: [91, 90, 511, 521, 551],
  2017: [91, 90, 511, 521, 551],
  2018: [91, 90, 531, 551],
  2019: [91, 90, 531, 551],
  2020: [91, 90, 501, 531, 551],
  2021: [91, 90, 501, 511, 521, 551],
  2022: [91, 90, 501, 511, 521, 551],
  2023: [91, 90, 501, 1191, 1201, 1211, 1],
  2024: [91, 90, 501, 1191, 1201, 1211, 1],
  2025: [91, 90, 1191, 1201, 99, 1],
  2026: [91, 90, 501, 1191, 1201, 1211, 1],
  2027: [91, 90, 501, 1191, 1201, 1211, 1],
  2028: [91, 90, 501, 1191, 1201, 1211, 1],
  2029: [91, 90, 501, 1191, 1201, 1211, 1],
  2030: [91, 90, 501, 1191, 1201, 1211, 1],
}


